import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Package, Clock } from "lucide-react";
import api, { formatINR } from "@/lib/api";

const OrderSuccess = () => {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderId) { setLoading(false); return; }
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-goldsoft" size={40} /></div>;
  if (!order) return <div className="mx-auto max-w-lg px-5 py-28 text-center"><p className="font-display text-3xl text-choco">Order not found</p><Link to="/shop" className="inline-block mt-6 text-goldsoft">← Back to shop</Link></div>;

  const verifying = order.payment_status === "submitted";

  return (
    <div className="mx-auto max-w-2xl px-5 sm:px-8 py-14 sm:py-20">
      <div data-testid="order-success" className="bg-white rounded-3xl border border-[#EFEBE1] p-8 sm:p-10 text-center">
        <div className={`h-16 w-16 rounded-full mx-auto flex items-center justify-center ${verifying ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
          {verifying ? <Clock size={36} /> : <CheckCircle2 size={38} />}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-choco mt-6">Order placed successfully!</h1>
        <p className="text-muted-foreground mt-2">
          {verifying ? "We've received your payment details and will confirm your order shortly after verification." : "Thank you for choosing Nel Bake House."}
        </p>

        <div className="mt-8 bg-cream rounded-2xl p-6 text-left">
          <div className="flex justify-between items-center pb-4 border-b border-[#EFEBE1]">
            <span className="text-sm text-muted-foreground">Order ID</span>
            <span data-testid="success-order-id" className="font-display text-2xl font-bold text-choco">{order.order_id}</span>
          </div>
          <div className="flex justify-between py-2 mt-2 text-sm">
            <span className="text-muted-foreground">Payment Status</span>
            <span className={`font-semibold capitalize ${verifying ? "text-amber-700" : "text-green-700"}`}>{verifying ? "Awaiting verification" : order.payment_status}</span>
          </div>
          <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">Order Status</span><span className="font-semibold text-choco">{order.status === "pending_payment" ? "Payment being verified" : order.status}</span></div>
          {order.payment_reference && <div className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">Payment Ref</span><span className="font-medium text-choco">{order.payment_reference}</span></div>}
          <div className="mt-4 space-y-2">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm"><span className="text-chocodark">{it.name} × {it.quantity}</span><span className="font-medium">{formatINR(it.line_total)}</span></div>
            ))}
          </div>
          <div className="border-t border-[#EFEBE1] mt-4 pt-3 flex justify-between font-semibold text-choco"><span>Total</span><span>{formatINR(order.total)}</span></div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Link to="/track-order" data-testid="success-track-btn" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark transition-colors"><Package size={18} /> Track Order</Link>
          <Link to="/shop" className="flex-1 inline-flex items-center justify-center rounded-full border-2 border-choco text-choco font-semibold px-6 py-3.5 hover:bg-choco hover:text-cream transition-colors">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
